import { useIncrementOnpremiseDtaFileNumber } from "apollo/hooks/mutations";
import { useEnvContext } from "context/EnvContext";
import {
  FILE_TABLE_ACTION_SYNC_VERSION_HIS,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
  TRACK_EVENTS,
} from "core/consts";
import { onPremHttpRequest } from "core/model/careseekers";
import { getMainIkNumber } from "core/model/companies";
import { ENV_PRODUCTION } from "core/model/config";
import {
  getFileMimeType,
  processDocumentFile,
  validateFile,
} from "core/model/files";
import { b64DecodeUnicode, b64EncodeUnicode } from "core/model/utils/base64";
import { getErrorMessage } from "core/model/utils/errors";
import {
  AHBMessageResponse,
  AHBMessagesPayload,
  AHBProcessingCode,
  AHBWithdrawFormValues,
  Auction,
  AuctionForm,
  Base64EncodedFile,
  FileCategory,
  FileHIS,
  FileHISStateful,
  Filev2Stateful,
  Patient,
  QueryProgress,
  QueryProgressActive,
  VzdDirectoryEntry,
} from "core/types";
import {
  GeneralListAction,
  SetAction,
} from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { useCopy } from "dsl/atoms/CopyButton/useCopy";
import { useHL7ImportStatusContext } from "dsl/atoms/HL7ImportStatusContext";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useDeleteFile } from "dsl/ecosystems/PatientFiles/useDeleteFile";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import {
  useIsEligiblePage,
  useUploadFile,
} from "dsl/ecosystems/PatientFiles/useUploadFile";
import { useCallback, useEffect, useState } from "react";
import { useTracking } from "react-tracking";
import { useLoggedCareseeker, useLoggedInAccountId } from "reduxentities/hooks";
import { useTranslations } from "translations";
import { getAHBWithdrawalSchema } from "../core/schemas/AHBWithdrawalSchema";
import { mapToRehabWithdrawal } from "./mapping/mapToRehabWithdrawal";
import { resolveArhVersion } from "./resolveArhVersion";
import {
  buildAarFilename,
  createAHBRequestXml,
  createValidatedXml,
} from "./utils";

export type AHBRequestProps = {
  auction: Auction;
  isApprovedByDoctor?: boolean;
  patient: Patient;
};

type AHBRequestBody = {
  attached_file_count: number;
  account_id: number;
  auction_id: number;
  careseeker_id: number;
  content: Base64EncodedFile;
  external_id: string;
  insurance_id: number;
  message_id: string;
  patient_id: number;
  receiver_kim: string;
  sender_kim: string;
  service_identifier_version: string;
  subject: string;
};

export type AHBWithdrawPartialPayload = AHBWithdrawFormValues & {
  documentNumber: number;
  hospital_ik_number: number;
  insurance_ik_number: number;
  kim_address_insurance: string;
  request_message_id: string;
  kim_address_hospital: string;
};

type AHBWithdrawRequestBody = AHBWithdrawFormValues & {
  account_id: number;
  auction_id: number;
  careseeker_id: number;
  content: Base64EncodedFile;
  external_id: string;
  insurance_id: number;
  message_id: string;
  patient_id: number;
  receiver_kim: string;
  sender_kim: string;
  service_identifier_version: string;
  subject: string;
};

// #TODO: could BE return an empty array instead?
const NULL_MESSAGE = "could not find any documents";

async function checkResponse(response: Response, context: string) {
  if (!response.ok) {
    let errorMessage = `[${context}]: Request failed with status ${response.status}`;
    try {
      const errorBody = await response.json();
      if (errorBody?.error) {
        errorMessage += ` - ${errorBody.error}`;
      }
    } catch (_e) {
      console.warn(`[${context}]: Unable to parse error response as JSON`);
    }
    throw new Error(errorMessage);
  }
}

function extractFilenameFromHeader(response: Response) {
  const filenamePattern = /filename=([^;]+)/;
  const contentDisposition = response.headers.get("Content-Disposition");
  const match = contentDisposition?.match(filenamePattern);
  const fileName = match?.[1].trim();
  if (!fileName) {
    throw new Error("[extractFilenameFromHeader] filename not found in header");
  }
  return fileName;
}

function assertAuctionReadyForOnpremiseDtaFileIncrement(auction: Auction) {
  const careseekerId = auction.patient?.careseeker?.id;
  const insuranceIkNumber =
    auction.patient?.profile?.financing?.insurance?.ik_number;
  const hospitalIkNumber = getMainIkNumber(
    auction.patient?.careseeker?.company,
  );
  if (!careseekerId || !insuranceIkNumber || !hospitalIkNumber) {
    throw new Error(
      `[useIncrementOnpremiseDtaFileNumber] careseekerId, insuranceIkNumber, and hospitalIkNumber are all required (auctionId=${String(
        auction.id,
      )}, careseekerId=${String(
        careseekerId ?? "",
      )}, insuranceIkNumber=${String(
        insuranceIkNumber ?? "",
      )}, hospitalIkNumber=${String(hospitalIkNumber ?? "")})`,
    );
  }
}

export function useImportFilesHIS({
  dispatchOnMount,
  externalId,
  fromArchive,
}: {
  dispatchOnMount?: boolean;
  externalId: string;
  fromArchive?: boolean;
}) {
  const queryParams = `from_archive=${Boolean(fromArchive)}`;
  const [queryProgress, setQueryProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [files, setFiles] = useState<FileHIS[]>();
  const { on_premise_authorization_token, on_premise_domain_ssl } =
    useHL7ImportStatusContext();

  async function fetchFiles() {
    setQueryProgress(QUERY_PROGRESS_PENDING);
    try {
      const response = await onPremHttpRequest({
        method: "GET",
        on_premise_authorization_token,
        on_premise_domain_ssl,
        path: `patients/${externalId}/documents?${queryParams}`,
      });
      await checkResponse(response, "useImportFilesHIS");
      const payload = await response.json();
      const isEmpty = !payload || payload === NULL_MESSAGE;
      setFiles(isEmpty ? [] : payload);
      setQueryProgress(QUERY_PROGRESS_SUCCEED);
    } catch (err) {
      console.error("[useImportFilesHIS]:", (err as Error).message);
      setQueryProgress(QUERY_PROGRESS_FAILED);
    }
  }

  useEffect(() => {
    if (dispatchOnMount) {
      fetchFiles();
    }
  }, []);

  return { fetchFiles, files, queryProgress };
}

export function useImportAndUploadFilesContentHIS({
  action,
  externalId,
  filesHIS,
  handleDeleteFile,
  handleUploadFile,
  setAction,
  skip,
}: {
  action: GeneralListAction<FileFeatureData | undefined> | undefined;
  externalId: string;
  filesHIS: FileHISStateful[] | undefined;
  handleDeleteFile: ReturnType<typeof useDeleteFile>["handleDeleteFile"];
  handleUploadFile: ReturnType<typeof useUploadFile>["handleUploadFile"];
  setAction: SetAction<FileFeatureData>;
  skip: boolean;
}) {
  const isEligiblePage = useIsEligiblePage();
  const toast = useToast();
  const translations = useTranslations();
  const { trackEvent } = useTracking();
  const { on_premise_authorization_token, on_premise_domain_ssl } =
    useHL7ImportStatusContext();

  async function fetchAndUploadFilesDetails({
    filesHISToImport,
    onComplete,
  }: {
    filesHISToImport: (Pick<
      FileHISStateful,
      | "document_category_number"
      | "document_category_string"
      | "document_id"
      | "id"
      | "share_mode"
    > & { fileV2Id?: Filev2Stateful["id"] })[];
    onComplete?: (params: {
      category: FileCategory;
      file: File;
      fileId: number;
    }) => Promise<void>;
  }) {
    try {
      await Promise.all(
        filesHISToImport.map(
          async ({
            document_category_number,
            document_category_string,
            document_id,
            fileV2Id,
            id,
            share_mode,
          }) => {
            const response = await onPremHttpRequest({
              method: "GET",
              on_premise_authorization_token,
              on_premise_domain_ssl,
              path: `patients/${externalId}/documents/${document_id}`,
            });
            await checkResponse(response, "useImportAndUploadFilesContentHIS");

            const document_file_name = extractFilenameFromHeader(response);
            const blob = await response.blob();
            const type = getFileMimeType({ filename: document_file_name });
            const file = new File([blob], document_file_name, { type });

            await validateFile({
              file,
              fromArchive: true,
              isEligiblePage,
              trackEvent,
              translations,
            });
            const content = await processDocumentFile(file);
            await handleUploadFile(
              {
                category: document_category_number,
                category_other: document_category_string,
                content,
                file,
                fileName: document_file_name,
                kis_document_id_string: id,
                share_mode,
              },
              {
                onComplete: onComplete
                  ? (createdFile) =>
                      onComplete({
                        category: document_category_number,
                        file,
                        fileId: createdFile.id,
                      })
                  : undefined,
              },
            );
            if (
              action?.type === FILE_TABLE_ACTION_SYNC_VERSION_HIS &&
              fileV2Id
            ) {
              await handleDeleteFile(fileV2Id);
            }
          },
        ),
      );
    } catch (err) {
      console.error("Unexpected import error:", (err as Error).message);
      toast({
        color: "danger",
        message: translations.fileUploader.fileCouldNotFetch,
      });
    } finally {
      setAction(undefined);
    }
  }

  useEffect(() => {
    if (!skip && filesHIS?.length) {
      fetchAndUploadFilesDetails({ filesHISToImport: filesHIS });
    }
  }, [skip, filesHIS]);

  return { fetchAndUploadFilesDetails };
}

export function useGetAHBMessageThreads({
  dispatchOnMount,
  externalId,
}: {
  dispatchOnMount?: boolean;
  externalId: string | undefined;
}) {
  const [progress, setProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [payload, setPayload] = useState<AHBMessagesPayload>();
  const { on_premise_authorization_token, on_premise_domain_ssl } =
    useHL7ImportStatusContext();

  const getAHBMessageThreads = async () => {
    setProgress(QUERY_PROGRESS_PENDING);
    try {
      if (!externalId) {
        throw new Error("externalId is required");
      }
      const response = await onPremHttpRequest({
        method: "GET",
        on_premise_authorization_token,
        on_premise_domain_ssl,
        path: `patients/${externalId}/messages`,
      });
      await checkResponse(response, "useGetAHBMessageThreads");

      const text = await response.text();
      const parsedPayload = text ? JSON.parse(text) : [];
      setPayload(parsedPayload ?? []);
      setProgress(QUERY_PROGRESS_SUCCEED);
    } catch (err) {
      console.error("[useGetAHBMessageThreads]:", (err as Error).message);
      setProgress(QUERY_PROGRESS_FAILED);
    }
  };

  useEffect(() => {
    if (dispatchOnMount) {
      getAHBMessageThreads();
    }
  }, [dispatchOnMount, externalId]);

  return [progress, payload, getAHBMessageThreads] as const;
}

export function useGetVzdDirectoryEntry() {
  const { on_premise_authorization_token, on_premise_domain_ssl } =
    useHL7ImportStatusContext();

  const getVzdDirectoryEntry = async (
    ik_number: number | undefined,
  ): Promise<VzdDirectoryEntry | null> => {
    if (!ik_number) {
      return null;
    }
    try {
      const response = await onPremHttpRequest({
        method: "GET",
        on_premise_authorization_token,
        on_premise_domain_ssl,
        path: `vzd/${ik_number}`,
      });
      if (!response.ok) {
        throw new Error("Failed to fetch VZD directory entry");
      }

      const payload = (await response.json()) as VzdDirectoryEntry | null;
      return payload ?? null;
    } catch (err) {
      console.error("[useGetVzdDirectoryEntry]:", (err as Error).message);
      return null;
    }
  };

  return [getVzdDirectoryEntry] as const;
}

export function useGetAHBResponse() {
  const [progress, setProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [ahbResponse, setAHBResponse] = useState<Base64EncodedFile>();

  const { on_premise_authorization_token, on_premise_domain_ssl } =
    useHL7ImportStatusContext();

  const getAHBResponse = useCallback(
    async (response_id: AHBMessageResponse["response_id"]) => {
      setProgress(QUERY_PROGRESS_PENDING);
      try {
        if (!response_id) {
          throw new Error("response_id is required");
        }

        const response = await onPremHttpRequest({
          method: "GET",
          on_premise_authorization_token,
          on_premise_domain_ssl,
          path: `responses/${response_id}/content`,
        });

        await checkResponse(response, "useGetAHBResponse");

        const payload: Base64EncodedFile =
          (await response.text()) as Base64EncodedFile;
        setProgress(QUERY_PROGRESS_SUCCEED);
        setAHBResponse(payload);

        return payload;
      } catch (err) {
        console.error("[useGetAHBResponse]:", (err as Error).message);
        setProgress(QUERY_PROGRESS_FAILED);
      }
    },
    [on_premise_authorization_token, on_premise_domain_ssl],
  );

  return [progress, getAHBResponse, ahbResponse] as const;
}

export function useGetXMLForMessageId() {
  const [progress, setProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const translations = useTranslations();
  const toast = useToast();
  const { copyToClipboard } = useCopy({
    copySuccessMessage:
      translations.insuranceApp.eligibilityTab.sendDocuments.withdrawModal
        .successXmlCopied,
    onCopyMessage:
      translations.insuranceApp.eligibilityTab.sendDocuments.copyXmlButton,
  });

  const { on_premise_authorization_token, on_premise_domain_ssl } =
    useHL7ImportStatusContext();

  const getXMLForMessageId = useCallback(
    async (message_id: AHBMessageResponse["message_id"]) => {
      setProgress(QUERY_PROGRESS_PENDING);
      try {
        if (!message_id) {
          throw new Error("message_id is required");
        }

        const response = await onPremHttpRequest({
          method: "GET",
          on_premise_authorization_token,
          on_premise_domain_ssl,
          path: `requests/${message_id}/content`,
        });

        await checkResponse(response, "useGetXMLForMessageId");
        const payload: Base64EncodedFile =
          (await response.text()) as Base64EncodedFile;
        const parsedPayload = JSON.parse(payload).trim();
        const decodedXML = b64DecodeUnicode(parsedPayload);
        await copyToClipboard(decodedXML);
        toast({
          color: "success",
          message:
            translations.insuranceApp.eligibilityTab.sendDocuments.withdrawModal
              .successXmlCopied,
        });
      } catch (err) {
        console.error("[useGetXMLForMessageId]:", (err as Error).message);
        toast({
          color: "danger",
          message: translations.general.error,
        });
        setProgress(QUERY_PROGRESS_FAILED);
      } finally {
        setProgress(QUERY_PROGRESS_NOT_STARTED);
      }
    },
    [
      on_premise_authorization_token,
      on_premise_domain_ssl,
      translations,
      toast,
      copyToClipboard,
    ],
  );

  return [progress, getXMLForMessageId] as const;
}

export function useSendAHBRequest({
  auction,
  isApprovedByDoctor,
  patient,
}: AHBRequestProps) {
  const { env } = useEnvContext();
  const { trackEvent } = useTracking();
  const toast = useToast();
  const translations = useTranslations();
  const loggedCareseeker = useLoggedCareseeker();
  const accountId = useLoggedInAccountId();
  const careseeker = useLoggedCareseeker();
  const [getVzdDirectoryEntry] = useGetVzdDirectoryEntry();
  const [incrementFileNumber] = useIncrementOnpremiseDtaFileNumber({
    auction,
  });
  const { on_premise_authorization_token, on_premise_domain_ssl } =
    loggedCareseeker?.config ?? {};
  const [progress, setProgress] = useState<QueryProgressActive>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  const insurance = patient.profile?.financing?.insurance;
  const insurance_id = insurance?.id;
  const insurance_ik_number = insurance?.ik_number;
  const kim_address_insurance = insurance?.kim_address;

  const sendAHBRequest = async ({
    auctionForms,
    files,
  }: {
    auctionForms: AuctionForm[];
    files?: (FileHISStateful | Filev2Stateful)[];
  }) => {
    setProgress(QUERY_PROGRESS_PENDING);
    try {
      if (!isApprovedByDoctor) {
        throw new Error("Medical form must be approved by doctor");
      }
      if (!patient.external_id) {
        throw new Error("external_id is required");
      }
      if (!loggedCareseeker?.kim_address) {
        throw new Error("hospital kim_address is required");
      }
      if (!kim_address_insurance && !insurance_ik_number) {
        throw new Error(
          "insurance kim_address or insurance ik_number is required",
        );
      }
      if (!insurance_id) {
        throw new Error("insurance_id is required");
      }
      if (!accountId) {
        throw new Error("accountId is required");
      }

      if (!careseeker?.id) {
        throw new Error("careseeker id is required");
      }

      const shouldUseVzdKimAddress =
        patient.profile?.financing?.insurance?.config?.use_vzd_kim_address;
      let insuranceEntry = null;

      if (shouldUseVzdKimAddress) {
        insuranceEntry = await getVzdDirectoryEntry(insurance_ik_number);
      }

      const finalInsuranceKim = shouldUseVzdKimAddress
        ? insuranceEntry?.mail ?? kim_address_insurance
        : kim_address_insurance;

      if (!finalInsuranceKim) {
        throw new Error(
          "[sendAHBRequest - finalInsuranceKim] insurance kim_address is required",
        );
      }

      assertAuctionReadyForOnpremiseDtaFileIncrement(auction);
      const { data } = await incrementFileNumber();
      const documentNumber = Number(data?.onpremiseDtaFileNumber.file_number);
      if (!documentNumber) {
        throw new Error("documentNumber is required");
      }

      const {
        json,
        schemaVersion: service_identifier_version,
        xml,
      } = await createAHBRequestXml({
        auctionForms,
        documentNumber,
        env,
        excludePatientAddress: true,
        externalId: patient.external_id,
        files,
        hospitalIkNumber: getMainIkNumber(patient.careseeker?.company),
        insuranceIkNumber: insurance_ik_number,
        on_premise_authorization_token,
        on_premise_domain_ssl,
        onInvalid() {
          toast({
            color: "danger",
            message: translations.patientForms.incompleteFormError,
          });
        },
        shouldValidate: true,
        trackEvent,
      });

      const header = json.Nutzdaten.Header;
      const message_id = header["gdb:Datei_ID"];
      const message_type = header["gdb:Nachrichtentyp"];
      const procedure_id = header["gdb:Verfahrenskennung"];
      const processing_code = json.Nutzdaten.Body.Anschlussrehabilitation[
        "aar:Antrag"
      ]["aar:Antrag"][
        "arh:Verarbeitungskennzeichen"
      ] satisfies AHBProcessingCode;

      /** ------------- For Testing Purposes --------------- */
      // const xslUrl = getStaticAsset("Stylesheet_GKHAR_1.1.0.xsl");
      // const res = await fetch(xslUrl);
      // if (!res.ok) throw new Error("Failed to load XSL");
      // const xslText = await res.text();

      // const pdfBytes = await transformXmlToPdf({ xmlText: xml, xslText });
      // openPdfInNewWindow(pdfBytes);
      // return

      /** ------------------------------------------------- */

      const body: AHBRequestBody = {
        account_id: accountId,
        attached_file_count: files?.length ?? 0,
        auction_id: auction.id,
        careseeker_id: careseeker.id,
        content: b64EncodeUnicode(xml) as Base64EncodedFile,
        external_id: patient.external_id,
        insurance_id,
        message_id,
        patient_id: patient.id,
        receiver_kim: finalInsuranceKim,
        sender_kim: loggedCareseeker.kim_address,
        service_identifier_version,
        subject: buildAarFilename({
          message_id,
          message_type,
          procedure_id,
          processing_code,
        }),
      };

      const response = await onPremHttpRequest({
        body: JSON.stringify(body),
        method: "POST",
        on_premise_authorization_token,
        on_premise_domain_ssl,
        path: `patients/${patient.external_id}/ahb_request`,
      });
      await checkResponse(response, "useSendAHBRequest");

      trackEvent({
        message_id,
        name: TRACK_EVENTS.AHB_SEND_REQUEST_SUCCESS,
      });
      toast({
        color: "success",
        message:
          translations.insuranceApp.eligibilityTab.sendDocuments.successMessage,
      });
    } catch (err) {
      trackEvent({
        error: getErrorMessage(err),
        name: TRACK_EVENTS.AHB_SEND_REQUEST_ERROR,
      });
      console.error("[useSendAHBRequest]:", getErrorMessage(err));
      toast({
        color: "danger",
        message:
          translations.insuranceApp.eligibilityTab.sendDocuments.errorMessage,
      });
    } finally {
      setProgress(QUERY_PROGRESS_NOT_STARTED);
    }
  };

  return [progress, sendAHBRequest] as const;
}

export function useWithdrawAHBRequest({ auction, patient }: AHBRequestProps) {
  const { env } = useEnvContext();
  const toast = useToast();
  const { trackEvent } = useTracking();
  const translations = useTranslations();
  const loggedCareseeker = useLoggedCareseeker();
  const accountId = useLoggedInAccountId();
  const careseeker = useLoggedCareseeker();
  const [getVzdDirectoryEntry] = useGetVzdDirectoryEntry();
  const [incrementFileNumber] = useIncrementOnpremiseDtaFileNumber({
    auction,
  });
  const { on_premise_authorization_token, on_premise_domain_ssl } =
    loggedCareseeker?.config ?? {};
  const [progress, setProgress] = useState<QueryProgressActive>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const insurance = patient.profile?.financing?.insurance;
  const insurance_ik_number = insurance?.ik_number;
  const hospital_ik_number = getMainIkNumber(patient.careseeker?.company);

  const withdrawAHBRequest = async (
    partialPayload: Omit<
      AHBWithdrawPartialPayload,
      "hospital_ik_number" | "insurance_ik_number" | "documentNumber"
    >,
  ) => {
    setProgress(QUERY_PROGRESS_PENDING);
    try {
      if (!accountId) {
        throw new Error("accountId is required");
      }
      if (!careseeker?.id) {
        throw new Error("careseeker id is required");
      }
      if (!patient.external_id) {
        throw new Error("external_id is required");
      }
      if (!partialPayload.kim_address_hospital) {
        throw new Error("hospital kim_address address is required");
      }
      if (!partialPayload.request_message_id) {
        throw new Error("request_message_id address is required");
      }
      if (!partialPayload.kim_address_insurance) {
        throw new Error("insurance kim_address is required");
      }
      if (!insurance?.id) {
        throw new Error("insurance_id is required");
      }
      if (!hospital_ik_number) {
        throw new Error("hospital_ik_number is required");
      }
      if (!insurance_ik_number) {
        throw new Error("insurance_ik_number is required");
      }

      assertAuctionReadyForOnpremiseDtaFileIncrement(auction);
      const { data } = await incrementFileNumber();
      const documentNumber = Number(data?.onpremiseDtaFileNumber.file_number);
      if (!documentNumber) {
        throw new Error("documentNumber is required");
      }

      const schemaVersion = resolveArhVersion(auction?.forms);

      const mappedPayload = mapToRehabWithdrawal({
        isProd: env === ENV_PRODUCTION,
        schemaVersion,
        unsanitizedForm: {
          ...partialPayload,
          documentNumber,
          hospital_ik_number,
          insurance_ik_number,
        },
      });

      const { json, xml } = await createValidatedXml({
        json: mappedPayload,
        schema: getAHBWithdrawalSchema(schemaVersion),
        shouldValidate: true,
        trackEvent,
      });

      const header = json.Nutzdaten.Header;
      const message_id = header["gdb:Datei_ID"];
      const message_type = header["gdb:Nachrichtentyp"];
      const procedure_id = header["gdb:Verfahrenskennung"];

      const shouldUseVzdKimAddress =
        patient.profile?.financing?.insurance?.config?.use_vzd_kim_address;
      let insuranceEntry = null;

      if (shouldUseVzdKimAddress) {
        insuranceEntry = await getVzdDirectoryEntry(insurance_ik_number);
      }

      const finalInsuranceKim = shouldUseVzdKimAddress
        ? insuranceEntry?.mail ?? partialPayload.kim_address_insurance
        : partialPayload.kim_address_insurance;

      if (!finalInsuranceKim) {
        throw new Error(
          "[withdrawAHBRequest - finalInsuranceKim] insurance kim_address is required",
        );
      }

      const body: AHBWithdrawRequestBody = {
        account_id: accountId,
        auction_id: auction.id,
        careseeker_id: careseeker.id,
        content: b64EncodeUnicode(xml) as Base64EncodedFile,
        external_id: patient.external_id,
        insurance_id: insurance.id,
        message_id,
        patient_id: patient.id,
        receiver_kim: finalInsuranceKim,
        sender_kim: partialPayload.kim_address_hospital,
        service_identifier_version: schemaVersion,
        subject: buildAarFilename({ message_id, message_type, procedure_id }),
        withdraw_reason: partialPayload.withdraw_reason,
        withdraw_reason_other: partialPayload.withdraw_reason_other,
      };

      const response = await onPremHttpRequest({
        body: JSON.stringify(body),
        method: "POST",
        on_premise_authorization_token,
        on_premise_domain_ssl,
        path: `patients/${patient.external_id}/messages/${encodeURIComponent(
          partialPayload.request_message_id,
        )}/withdraw`,
      });
      await checkResponse(response, "useWithdrawAHBRequest");

      trackEvent({
        message_id,
        name: TRACK_EVENTS.AHB_WITHDRAW_REQUEST_SUCCESS,
      });
      toast({
        color: "success",
        message:
          translations.insuranceApp.eligibilityTab.sendDocuments.withdrawModal
            .withdrawSuccess,
      });
    } catch (err) {
      trackEvent({
        error: getErrorMessage(err),
        name: TRACK_EVENTS.AHB_WITHDRAW_REQUEST_ERROR,
      });
      console.error("[useWithdrawAHBRequest]:", getErrorMessage(err));
      toast({
        color: "danger",
        message:
          translations.insuranceApp.eligibilityTab.sendDocuments.withdrawModal
            .withdrawError,
      });
    } finally {
      setProgress(QUERY_PROGRESS_NOT_STARTED);
    }
  };

  return [progress, withdrawAHBRequest] as const;
}
